import { BarChart3, Database, Eye, Trash2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
  Card,
  CardContent,
  CardHeader,
} from "../../../../../shared/components/card/Card";
import { Button } from "../../../../../shared/components/button/Button";
import { Badge } from "../../../../../shared/components/badge/Badge";
import { resolveDatasetsCentreDetailRoute } from "../../../../../shared/lib/routesResolver";

const DatasetCard = ({ dataset, onDelete }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  return (
    <Card className="hover:shadow-lg dark:hover:shadow-xl transition-shadow duration-200 bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-lg text-gray-900 dark:text-gray-100 truncate mb-1">
              {dataset.name}
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 line-clamp-2 mb-3">
              {dataset.description}
            </p>
          </div>
          <div className="flex flex-col items-end gap-2 ml-3">
            <Badge
              variant={dataset.metrics > 0 ? "default" : "secondary"}
              className={`text-xs ${
                dataset.metrics > 0
                  ? "bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 border-blue-200 dark:border-blue-800"
                  : "bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200 border-gray-200 dark:border-gray-600"
              }`}
            >
              {dataset.metrics}{" "}
              {t("datasetsCentre.common.metrics").toLowerCase()}
            </Badge>
            <Badge
              variant={dataset.active ? "default" : "secondary"}
              className={`text-xs ${
                dataset.active
                  ? "bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200 border-green-200 dark:border-green-800"
                  : "bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200 border-red-200 dark:border-red-800"
              }`}
            >
              {dataset.active
                ? t("datasetsCentre.common.active")
                : t("datasetsCentre.common.inactive")}
            </Badge>
          </div>
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div className="flex items-center gap-2">
              <Database className="h-4 w-4 text-gray-400 dark:text-gray-500" />
              <span className="text-gray-600 dark:text-gray-400">
                {dataset.records}{" "}
                {t("datasetsCentre.common.records").toLowerCase()}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <BarChart3 className="h-4 w-4 text-gray-400 dark:text-gray-500" />
              <span className="text-gray-600 dark:text-gray-400">
                {dataset.dimensions}{" "}
                {t("datasetsCentre.common.dimensions").toLowerCase()}
              </span>
            </div>
          </div>

          <div className="flex justify-between pt-2">
            <Button
              variant="outline"
              className="w-[48%] mt-4 bg-transparent hover:cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 border-gray-200 dark:border-gray-600 text-gray-900 dark:text-gray-100"
              size="sm"
              onClick={() =>
                navigate(resolveDatasetsCentreDetailRoute(dataset.id), {
                  state: { id: dataset.id },
                })
              }
            >
              <Eye className="h-4 w-4 mr-2" />
              {t("datasetsCentre.common.viewDetails")}
            </Button>

            <Button
              variant="outline"
              className="w-[48%] mt-4 bg-transparent hover:bg-red-50 dark:hover:bg-red-900/20 border-gray-200 dark:border-gray-600 text-red-600 dark:text-red-400 hover:border-red-300 dark:hover:border-red-700"
              size="sm"
              onClick={() => onDelete(dataset.id)}
            >
              <Trash2 className="h-4 w-4 mr-2" />
              {t("datasetsCentre.datasets.deleteButton")}
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default DatasetCard;
